/**
 * Overrides programáticos al style base de MapTiler para la estética
 * táctica del tema C4i. Se aplica SOLO cuando data-theme === 'tactical'.
 *
 * Estrategia: itera los style.layers que provee MapTiler (los que NO son
 * nuestros — filtramos por prefijo) y parcha color/opacity de backgrounds,
 * fills (agua, edificios, landuse) y lines (carreteras, ríos, límites).
 * Labels de ciudades se repintan en phosphor green con halo negro para el
 * look "radar".
 *
 * No mantiene estado para revertir: el cambio de tema siempre reloadea la
 * página (persistAndReload), así que en no-tactical los valores originales
 * del MapTiler style vuelven intactos.
 *
 * Se llama después de addAllLayers y attenuateExternalLabels — el paint
 * property de text-opacity que setea attenuate en labels de ciudades se
 * preserva (nosotros solo tocamos text-color y text-halo aquí para esas
 * layers, NO text-opacity, para no romper la atenuación por jurisdicción).
 */

// Paleta tactical del mapa base. Valores inspirados en el mockup del
// inversionista — negro puro de fondo, grises muy dim para carreteras,
// agua casi negra con tinte azul tenue.
var TACTICAL_MAP_PALETTE = {
  background:   '#000000',
  water:        '#020608',
  waterway:     '#020608',
  landuse:      '#06090D',
  building:     '#0A0E12',
  roadMinor:    '#0E1319',
  roadMain:     '#151C26',
  roadHighway:  '#1F2A3D',
  labelPlace:   '',            // se resuelve en runtime con --kln-accent
  labelDim:     'rgba(140, 180, 160, 0.45)',
  labelMinor:   'rgba(90, 120, 110, 0.35)',
  halo:         '#000000',
  adminBoundary: 'rgba(0, 255, 136, 0.18)',
}

// Prefijos de layers propias — no tocar estas (tienen su propio paint).
var OUR_LAYERS_RE = /^(unified-|jurisdiction-|trail-|geofence-|cluster-|heatmap-)/

/**
 * Aplica el estilo táctico al mapa. Idempotente — llamar varias veces no
 * tiene efecto incremental (siempre escribe los mismos paint properties).
 */
export function applyTacticalMapStyle(map) {
  if (!map || !map.getStyle) return
  // Solo aplicar cuando el tema activo es tactical. Chequeo contra el
  // data-theme que setea applyTheme() en <html>.
  var activeTheme = document.documentElement.getAttribute('data-theme')
  if (activeTheme !== 'tactical') return

  var style = map.getStyle()
  if (!style || !Array.isArray(style.layers)) return

  var accent = getComputedStyle(document.documentElement)
    .getPropertyValue('--kln-accent').trim() || '#00FF88'
  TACTICAL_MAP_PALETTE.labelPlace = accent

  for (var i = 0; i < style.layers.length; i++) {
    _patchLayer(map, style.layers[i])
  }
}

function _patchLayer(map, layer) {
  var id = layer.id
  if (!id || OUR_LAYERS_RE.test(id)) return

  try {
    if (layer.type === 'background') {
      _setPaint(map, id, 'background-color', TACTICAL_MAP_PALETTE.background)
      _setPaint(map, id, 'background-opacity', 1)
    }
    else if (layer.type === 'fill') {
      _patchFillLayer(map, id)
    }
    else if (layer.type === 'line') {
      _patchLineLayer(map, id)
    }
    else if (layer.type === 'symbol') {
      _patchSymbolLayer(map, id, layer)
    }
  } catch (e) {
    // Layer no admite la paint property que intentamos setear — skip.
  }
}

function _patchFillLayer(map, id) {
  if (/water|sea|ocean|lake/i.test(id)) {
    _setPaint(map, id, 'fill-color', TACTICAL_MAP_PALETTE.water)
    _setPaint(map, id, 'fill-opacity', 1)
  }
  else if (/building/i.test(id)) {
    _setPaint(map, id, 'fill-color', TACTICAL_MAP_PALETTE.building)
    _setPaint(map, id, 'fill-opacity', 0.55)
  }
  else if (/landuse|landcover|park|cemetery|sand|pedestrian|airport|pitch|aeroway|residential|commercial|industrial|hospital/i.test(id)) {
    _setPaint(map, id, 'fill-color', TACTICAL_MAP_PALETTE.landuse)
    _setPaint(map, id, 'fill-opacity', 0.5)
  }
}

function _patchLineLayer(map, id) {
  if (/water|river|stream|waterway/i.test(id)) {
    _setPaint(map, id, 'line-color', TACTICAL_MAP_PALETTE.waterway)
  }
  else if (/motorway|trunk|highway|_hw/i.test(id)) {
    _setPaint(map, id, 'line-color', TACTICAL_MAP_PALETTE.roadHighway)
  }
  else if (/primary|secondary/i.test(id)) {
    _setPaint(map, id, 'line-color', TACTICAL_MAP_PALETTE.roadMain)
  }
  else if (/road|street|path|minor|tertiary|service|track|footway|cycleway|link/i.test(id)) {
    _setPaint(map, id, 'line-color', TACTICAL_MAP_PALETTE.roadMinor)
  }
  else if (/building/i.test(id)) {
    _setPaint(map, id, 'line-color', TACTICAL_MAP_PALETTE.building)
  }
  else if (/boundary|admin|country|state/i.test(id)) {
    _setPaint(map, id, 'line-color', TACTICAL_MAP_PALETTE.adminBoundary)
  }
  else if (/bridge|tunnel/i.test(id)) {
    _setPaint(map, id, 'line-color', TACTICAL_MAP_PALETTE.roadMain)
  }
}

function _patchSymbolLayer(map, id, layer) {
  var hasText = layer.layout && layer.layout['text-field']
  if (!hasText) return

  // POIs: ya NO se aplastan. Antes se atenuaban a 0.12 por ser "ruido visual",
  // pero ahora las capas POI están zoom-gated (minzoom 14+, ver
  // tiles-styles-build.mjs) → solo aparecen a zoom profundo, justo donde el
  // operador necesita ver negocios/lugares para orientar. Se dejan con el
  // color/halo que les puso el graft (legible sobre el fondo tactical).
  if (/poi/i.test(id)) {
    return
  }

  // Place labels (ciudades, pueblos, capitales) — phosphor green con halo
  // negro denso. NO tocamos text-opacity para preservar la atenuación por
  // jurisdicción de attenuateExternalLabels.
  if (/place-city|place-town|place-capital|place-village|continent|country-name/i.test(id)) {
    _setPaint(map, id, 'text-color', TACTICAL_MAP_PALETTE.labelPlace)
    _setPaint(map, id, 'text-halo-color', TACTICAL_MAP_PALETTE.halo)
    _setPaint(map, id, 'text-halo-width', 2)
    _setPaint(map, id, 'text-halo-blur', 1)
    return
  }

  // Place secundarias (suburbios, barrios) — phosphor dim.
  if (/place|housenumber|address|neighbourhood|suburb/i.test(id)) {
    _setPaint(map, id, 'text-color', TACTICAL_MAP_PALETTE.labelDim)
    _setPaint(map, id, 'text-halo-color', TACTICAL_MAP_PALETTE.halo)
    return
  }

  // Road labels — gris muy dim para no distraer.
  if (/road|street|waterway-label/i.test(id)) {
    _setPaint(map, id, 'text-color', TACTICAL_MAP_PALETTE.labelMinor)
    _setPaint(map, id, 'text-halo-color', TACTICAL_MAP_PALETTE.halo)
    return
  }
}

// Wrapper try/catch: algunas layers de MapTiler no soportan una property
// específica (p.ej. un layer de símbolo no tiene background-opacity). En
// lugar de romper por el primer fail, lo skipeamos silenciosamente.
function _setPaint(map, id, prop, value) {
  try { map.setPaintProperty(id, prop, value) }
  catch (e) { /* silent */ }
}
